import { LoaderPage } from '@/components/LoaderPage';
import { toast } from '@/components/toast';
import { useAllVoices } from '@/hooks/useAllVoices';
import usePageTitle from '@/hooks/usePageTitle';
import { useGetCurrentAgentsPresenter } from '@/pages/agents/hooks';
import { CampaignCreateContextProvider } from '@/pages/campaign/CreatePage/context';
import { CampaignFormValues } from '@/pages/campaign/CreatePage/types';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import useTtsData from '@/services/tts/tokenManager';
import { languageToVoicesMap } from '@/state/presenters';
import { AppRoutes } from '@/utils/types';
import { ImageType } from '@d-id/types';
import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import { observer } from 'mobx-react-lite';
import { customAlphabet } from 'nanoid';
import { useEffect, useState } from 'react';
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { DEFAULT_CAMPAIGN_NAME } from '../constants';
import { Content, Header } from './components';
import { useClearForm } from './hooks';
import schema from './schema';
import { deleteExistingDraftAgent, onFormSubmit } from './utils';

const InnerEditorContainer = styled.form`
    background-color: ${({ theme }) => theme.colors.black[50]};
    overflow: auto;
    min-height: 100%;
    display: flex;
    flex-direction: column;
`;

const TITLE = 'Create Video Campaign - D-ID';

const CampaignCreatePage = observer(() => {
    const mixpanel = useAnalytics();
    const { data = [] } = useAllVoices();
    const presenter = useGetCurrentAgentsPresenter();
    const clearForm = useClearForm();
    const navigate = useNavigate();
    const { data: ttsData } = useTtsData();
    const languageToVoices = useRecoilValue(languageToVoicesMap);
    usePageTitle(TITLE);

    const formMethods = useForm<CampaignFormValues>({
        defaultValues: {
            id: `cmp_${customAlphabet('1234567890abcdefghijklmnopqrstuvwxyz', 21)()}`,
            name: DEFAULT_CAMPAIGN_NAME,
            voice: data?.[0],
            cta_enabled: true,
            presenter,
            script_fields: [],
            is_stitch: true,
        },
        shouldFocusError: false,
        mode: 'onSubmit',
        resolver: yupResolver(schema),
    });

    const onSubmit = (data: CampaignFormValues) =>
        onFormSubmit({
            data,
            ttsData,
            languageToVoices,
            onSuccess: () => {
                mixpanel?.track('campaigns-create', { event: 'success', campaign: data });
                clearForm({
                    removeAgentFromDB: false,
                });
                navigate(AppRoutes.Campaigns);
            },
            onError: () => {
                mixpanel?.track('campaigns-create', { event: 'fail', campaign: data });
            },
        });

    const onFailSubmit: SubmitErrorHandler<CampaignFormValues> = errors => {
        const errorMessage = Object.values(errors).map(error => error.message);

        toast.error({
            title: 'Invalid campaign data: ',
            message: errorMessage.join('\n'),
            options: {
                style: {
                    whiteSpace: 'break-spaces',
                    width: 'auto',
                    left: 0,
                },
            },
        });
    };

    return (
        <FormProvider {...formMethods}>
            <CampaignCreateContextProvider>
                <InnerEditorContainer
                    onSubmit={formMethods.handleSubmit(onSubmit, onFailSubmit)}
                    onKeyDown={e => e.key === 'Enter' && e.preventDefault()}>
                    <Header />
                    <Content />
                </InnerEditorContainer>
            </CampaignCreateContextProvider>
        </FormProvider>
    );
});

const DataWrapperCampaignCreatePage = observer(() => {
    const { isLoading: isLoadingVoices, data: voices } = useAllVoices();
    const [isDeletingDraftAgent, setDeletingDraftAgent] = useState(true);
    const presenter = useGetCurrentAgentsPresenter();
    const { isAnonymous } = useAuth();

    useEffect(() => {
        if (isAnonymous) {
            return;
        }

        deleteExistingDraftAgent().then(() => {
            setDeletingDraftAgent(false);
        });
    }, [isAnonymous]);

    if (isAnonymous) {
        return <Navigate to={AppRoutes.Home} />;
    }

    if (isLoadingVoices || !voices?.length || isDeletingDraftAgent || !presenter || presenter?.presenterType === ImageType.Skeleton) {
        return <LoaderPage />;
    }

    return <CampaignCreatePage />;
});

export { DataWrapperCampaignCreatePage as CampaignCreatePage };
